<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  size: {
    type: String,
    default: 'sm',
    validator: (value) => {
      return ['sm', 'base'].includes(value)
    }
  },
  currentPage: {
    type: Number,
    required: true,
    default: 0
  },
  totalPages: {
    type: Number,
    required: true,
    default: 0
  },
  minItemsCount: {
    type: Number,
    default: 0
  },
  maxItemsCount: {
    type: Number,
    default: 0
  },
  totalItemsCount: {
    type: Number,
    default: 0
  },
  paginationType: {
    type: String,
    default: 'default',
    validator: (value) => {
      // Default uses the standard items counter (e.g. "1-20 of 100"),
      // Manual allows for manually enabling Previous/Next buttons
      return ['default', 'manual'].includes(value)
    }
  },
  isPreviousDisabled: {
    type: Boolean,
    default: false
  },
  isNextDisabled: {
    type: Boolean,
    default: false
  },
  previousButtonLabel: {
    type: String,
    default: null
  },
  nextButtonLabel: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['pageSelect'])

const paginationItems = ref([]);

const paginatorItemClass = computed(() => ({
  'flex items-center justify-center leading-tight text-gray-500 bg-white border border-gray-300 cursor-pointer': true,
  'h-8 px-3': props.size === 'sm',
  'h-10 px-4': props.size === 'base'
}));

const paginatorCounterI18n = computed(() => {
  if (props.totalItemsCount > 0) {
    return 'texts.tablePageCountWTotal';
  } else if (props.minItemsCount > 0 || props.maxItemsCount > 0) {
    return 'texts.tablePageCountWoutTotal';
  } else {
    return null;
  }
});

watch(
  () => [props.currentPage, props.totalPages],
  ([newCurrent, newTotal]) => {
    paginationItems.value = getPagination(newCurrent, newTotal);
  },
  { immediate: true }
);

/**
 * Generate pagination buttons for displaying a range of pages
 * @param current {number} - The current page number
 * @param total {number} - The total number of pages
 * @returns {(string | number)[]} - An array of page numbers and ellipses
 */
function getPagination (current, total) {
  const ellipsis = '…';
  const maxButtons = 5;

  // Always show at least [1] and [total]
  if (total <= maxButtons) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = [1]; // always include first
  const middleCount = maxButtons - 2; // slots for middle window
  const half = Math.floor(middleCount / 2);

  let start = current - half;
  let end = current + (middleCount - half - 1);

  // Clamp within valid range
  if (start < 2) {
    start = 2;
    end = start + middleCount - 1;
  }
  if (end > total - 1) {
    end = total - 1;
    start = end - middleCount + 1;
  }

  // Add left ellipsis if needed
  if (start > 2) {
    pages.push(ellipsis);
  }

  // Add middle window
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Add right ellipsis if needed
  if (end < total - 1) {
    pages.push(ellipsis);
  }

  // Always include last
  pages.push(total);

  return pages;
}

const disablePrevious = computed(() => props.paginationType === 'manual'
  ? props.isPreviousDisabled
  : props.currentPage <= 1
);

const disableNext = computed(() => props.paginationType === 'manual'
  ? props.isNextDisabled
  : props.currentPage >= props.totalPages
);

/**
 * Handle page selection and emit event
 * @param page {number | string} - The page number to select
 */
function onPageSelected (page) {
  if (typeof page !== 'number' || page === props.currentPage) {
    return;
  }

  emit('pageSelect', page);
}
</script>

<template>
  <div class="flex items-center gap-5">
    <!-- Paginator Counter -->
    <span
      v-if="!!paginatorCounterI18n"
      class="text-sm text-gray-500 leading-[1.5]"
      data-component-element-id="paginator-counter"
    >
      <i18n-t
        :keypath="paginatorCounterI18n"
        tag="span"
      >
        <template #min>
          <span
            class="text-gray-900"
            data-component-element-id="paginator-counter-min"
          >
            {{ minItemsCount }}
          </span>
        </template>
        <template #separator>
          <span
            class="text-gray-900"
            data-component-element-id="paginator-counter-separator"
          >
            -
          </span>
        </template>
        <template #max>
          <span
            class="text-gray-900"
            data-component-element-id="paginator-counter-max"
          >
            {{ maxItemsCount }}
          </span>
        </template>
        <template #total v-if="props.totalItemsCount > 0">
          <span
            class="text-gray-900"
            data-component-element-id="paginator-counter-total"
          >
            {{ totalItemsCount }}
          </span>
        </template>
      </i18n-t>
    </span>

    <!-- Buttons -->
    <ul
      data-component-element-id="paginator-list"
      class="flex items-center -space-x-px"
      :class="{
        'h-8 text-sm': size === 'sm',
        'h-10 text-base': size === 'base'
      }"
    >
      <li
        data-component-element-id="paginator-previous"
      >
        <button
          class="ms-0 border-e-0 rounded-s-md disabled:text-gray-300 cursor-pointer"
          :class="[
            paginatorItemClass,
            { 'hover:bg-gray-100 hover:text-gray-700': currentPage > 1 },
          ]"
          :disabled="disablePrevious"
          data-component-element-id="paginator-previous-btn"
          @click="onPageSelected(currentPage - 1)"
        >
          <span v-if="previousButtonLabel">{{ previousButtonLabel }}</span>
          <svg
            v-else
            class="rtl:rotate-180"
            :class="{
              'w-2.5 h-2.5': size === 'sm',
              'w-3 h-3': size === 'base'
            }"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 1 1 5l4 4"/>
          </svg>
        </button>
      </li>
      <li
        v-for="(item, index) in paginationItems"
        :key="index"
        data-component-element-id="paginator-item"
      >
        <button
          :class="[
            paginatorItemClass,
            {
              'hover:bg-gray-100 hover:text-gray-700': item !== currentPage && typeof item === 'number',
              'z-10 !bg-gray-300 text-gray-900 cursor-default !hover:bg-transparent': item === currentPage,
              'text-base cursor-default !hover:bg-transparent': typeof item === 'string',
            }
          ]"
          :disabled="item === currentPage || typeof item === 'string'"
          data-component-element-id="paginator-item-btn"
          @click="onPageSelected(item)"
        >
          {{ item }}
        </button>
      </li>
      <li
        data-component-element-id="paginator-next"
      >
        <button
          class="rounded-e-md disabled:text-gray-300 cursor-pointer"
          :class="[
            paginatorItemClass,
            { 'hover:bg-gray-100 hover:text-gray-700': currentPage < totalPages },
          ]"
          :disabled="disableNext"
          data-component-element-id="paginator-next-btn"
          @click="onPageSelected(currentPage + 1)"
        >
          <span v-if="nextButtonLabel">{{ nextButtonLabel }}</span>
          <svg
            v-else
            class="rtl:rotate-180"
            :class="{
              'w-2.5 h-2.5': size === 'sm',
              'w-3 h-3': size === 'base'
            }"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 9 4-4-4-4"/>
          </svg>
        </button>
      </li>
    </ul>
  </div>
</template>
